import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import {
  PageTitleAndSubTitle,
  Breadcrumbs,
  OverviewCard,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Loader from '../../_components/molecules/Loader';
import { useNavigate } from 'react-router-dom';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import TabSelection from '../../_components/molecules/TabSelection';
import Snackbar from '../../_components/atoms/Snackbar';
import { observer } from 'mobx-react';
import { HOME_SCREEN } from '../../core-utils/routes';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import CareNavigatorConstants from '../../core-utils/messages/CareNavigatorConstants';
import CasesTab from '../../_components/organisms/CasesTab';
import PatientsTab from '../../_components/organisms/PatientsTab';
import CareNavigatorsTab from '../../_components/organisms/CareNavigatorsTab';
import RatingReviewTab from '../../_components/organisms/RatingReviewTab';
import { useStores } from '../../stores';
import axios from 'axios';
import properties from '../../core-utils/properties';
import FeatureScreenAccess from '../../_components/organisms/FeatureScreenAccess';
import { isFeatureAccessible } from '../../core-utils/helper-utils/userAccess';
// import RiskManagementTab from '../../_components/organisms/RiskManagementTab';

export interface CareNavigatorPageProps {}

const CareNavigatorPage: React.FC<CareNavigatorPageProps> = observer(() => {
  const useStyles = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
      padding: '40px 0px 66px 63px',
    },
    headerText: {
      fontSize: '12px',
      fontHeight: '12px',
      fontWeight: 500,
      color: patientAccessTheme.palette.text.highemphasis,
    },
  });

  const { classes } = useStyles();
  const navigate = useNavigate();

  const messages = CareNavigatorConstants.CARE_NAVIGATOR_PAGE;

  //Stores
  const careNavigatorStore = useStores().careNavigatorStore;
  const loginPageStore = useStores().loginPageStore;
  const userAccess = loginPageStore.userResponse;
  const combinedScreens = Array.from(
    new Set([
      ...(userAccess.careNavigationScreens || []),
      ...(userAccess.complianceAssuranceScreens || []),
      ...(userAccess.riskManagementScreens || []),
    ]),
  );
  const userDetailsStore = useStores().userDetailsStore;

  //Other States
  const [loading, setLoading] = useState<boolean>(true);
  const [loader] = useState<any>(<Loader />);
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [summaryData, setSummaryData] = useState<any>({
    totalInteractions: 0,
    totalCareNavigators: 0,
    activeCases: 0,
    closedCases: 0,
  });
  const isCareNavigator = loginPageStore.isCareNavigator;
  const tabs = isCareNavigator
    ? messages.tabList.slice(0, 2)
    : userDetailsStore.riskManagement
    ? messages.tabList
    : messages.tabList.slice(0, 4);

  const handleSelectedIndex = (index: number) => {
    setSelectedTabIndex(index);
  };

  // Snackbar
  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });

  const getSummary = async () => {
    if (!isFeatureAccessible('CARE_NAVIGATION', combinedScreens, userAccess)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.groupsBaseURL +
          properties.getGroupSummary,
        {
          group_id: careNavigatorStore.group_id,
        },
      )
      .then((response) => {
        if (response.status === 200 && response.data?.data) {
          setSummaryData(response.data.data);
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
    setLoading(false);
  };

  useEffect(() => {
    getSummary();
  }, []);

  return (
    <>
      {loading ? (
        loader
      ) : (
        <Grid container className={classes.root}>
          <FeatureScreenAccess
            noAccess={isFeatureAccessible(
              'CARE_NAVIGATION',
              combinedScreens,
              userAccess,
            )}
          >
            <Grid
              style={{
                marginBottom: '36px',
                width: '1182px',
              }}
            >
              <Grid style={{ marginBottom: '19px' }}>
                <Breadcrumbs
                  itemList={[
                    [
                      'text',
                      'Home Page',
                      {
                        color: patientAccessTheme.palette.primary[500],
                        fontWeight: 500,
                      },
                      () => {
                        navigate(HOME_SCREEN);
                      },
                    ],
                    [
                      'text',
                      'Care Navigator',
                      {
                        color: patientAccessTheme.palette.text.highemphasis,
                        fontWeight: 600,
                      },
                    ],
                  ]}
                />
              </Grid>
              <Grid style={{ position: 'relative' }}>
                <PageTitleAndSubTitle
                  title={
                    <Typography
                      fontSize="18px"
                      lineHeight="22px"
                      fontType="semiBold"
                      textColor={patientAccessTheme.palette.text.highemphasis}
                      margin="0px 8px 0px 0px"
                    >
                      {messages.title}
                    </Typography>
                  }
                  subTitle={messages.subTitle}
                />
              </Grid>
              <Grid style={{ display: 'flex', margin: '29px 0px' }}>
                <Grid style={{ marginRight: '24px' }}>
                  <OverviewCard
                    title={messages.topCards.engagements}
                    value={
                      <Typography
                        fontSize="20px"
                        lineHeight="43px"
                        fontType="medium"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                      >
                        {summaryData.totalInteractions}
                      </Typography>
                    }
                    rootStyles={{
                      width: '276px',
                      height: '100px',
                    }}
                    showClickHere
                  />
                </Grid>
                <Grid style={{ marginRight: '24px' }}>
                  <OverviewCard
                    title={messages.topCards.careNavigators}
                    value={
                      <Typography
                        fontSize="20px"
                        lineHeight="43px"
                        fontType="medium"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                      >
                        {summaryData.totalCareNavigators}
                      </Typography>
                    }
                    rootStyles={{
                      width: '276px',
                      height: '100px',
                    }}
                    showClickHere
                  />
                </Grid>
                <Grid style={{ marginRight: '24px' }}>
                  <OverviewCard
                    title={messages.topCards.casesOpen}
                    value={
                      <Typography
                        fontSize="20px"
                        lineHeight="43px"
                        fontType="medium"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                      >
                        {summaryData.activeCases}
                      </Typography>
                    }
                    rootStyles={{
                      width: '276px',
                      height: '100px',
                    }}
                    showClickHere
                  />
                </Grid>
                <Grid style={{ marginRight: '24px' }}>
                  <OverviewCard
                    title={messages.topCards.casesClosed}
                    value={
                      <Typography
                        fontSize="20px"
                        lineHeight="43px"
                        fontType="medium"
                        textColor={patientAccessTheme.palette.text.highemphasis}
                      >
                        {summaryData.closedCases}
                      </Typography>
                    }
                    rootStyles={{
                      height: '100px',
                      width: '276px',
                    }}
                    showClickHere
                  />
                </Grid>
              </Grid>
              <TabSelection
                selectedTabIndex={selectedTabIndex}
                setSelectedTabIndex={handleSelectedIndex}
                tabList={tabs}
              />
            </Grid>

            <Grid>
              {selectedTabIndex === 0 && <CasesTab />}
              {selectedTabIndex === 1 && <PatientsTab />}
              {selectedTabIndex === 2 && <CareNavigatorsTab />}
              {selectedTabIndex === 3 && <RatingReviewTab />}
              {/* {selectedTabIndex === 4 && <RiskManagementTab />} */}
            </Grid>
          </FeatureScreenAccess>
          <Snackbar
            {...snackbarState}
            handleClose={handleCloseSnackBar}
            vertical="bottom"
            horizontal="right"
          />
        </Grid>
      )}
    </>
  );
});

export default CareNavigatorPage;
